import { memo, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import ModalPortal from '../components/ModalPortal.jsx'

// Constants outside component to prevent recreation
const FAQ_ITEMS = [
  {
    id: 1,
    question: 'How do I add a new device to my account?',
    answer: 'Click the "Add Device" button in the top navigation or fleet overview, then enter your device serial number and follow the pairing wizard steps.',
  },
  {
    id: 2,
    question: 'Why is my device showing as offline?',
    answer: 'Devices may appear offline due to network connectivity issues, power outages, or configuration problems. Check your device\'s network connection and power status first.',
  },
  {
    id: 3,
    question: 'How often is device data updated?',
    answer: 'Device data is updated every 5 seconds by default. You can adjust this interval in your Preferences or manually refresh data using the refresh button.',
  },
  {
    id: 4,
    question: 'Can I export historical data?',
    answer: 'Yes, you can export data from the Analysis page. Select your desired time range and data types, then click the Export button to download a CSV file.',
  },
  {
    id: 5,
    question: 'How do I change my account email?',
    answer: 'Go to Account Settings from the user menu, click "Change Email Address", enter your new email and current password, then follow the verification steps.',
  },
  {
    id: 6,
    question: 'What should I do if I forgot my password?',
    answer: 'On the login page, click "Forgot Password" and enter your email address. You\'ll receive a password reset link via email.',
  },
  {
    id: 7,
    question: 'How do I manage device alerts and notifications?',
    answer: 'Device alerts appear in the notification bell. You can configure notification preferences in the Preferences page to customize which alerts you receive.',
  },
  {
    id: 8,
    question: 'Is my device data secure?',
    answer: 'Yes, all data is encrypted in transit and at rest. We use industry-standard security practices including Firebase Authentication and secure cloud infrastructure.',
  },
]

// Styles moved to constants to prevent recreation
const STYLES = {
  container: { padding: '20px' },
  card: {
    border: 'none',
    borderRadius: '16px',
    boxShadow: 'var(--shadow-card)',
    background: 'var(--bg-header-gradient)',
  },
  cardBody: { padding: '24px' },
  title: {
    fontSize: '1.5rem',
    fontWeight: '600',
    color: 'var(--text-dark)',
    borderBottom: '2px solid var(--border-color-light-gray)',
    paddingBottom: '12px',
  },
  inputGroup: {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color-light-gray)',
    borderRadius: '8px 0 0 8px',
  },
  input: { 
    border: '1px solid var(--border-color-light-gray)',
    borderRadius: '0 8px 8px 0',
    borderLeft: 'none',
  },
  clearButton: { 
    borderRadius: '0 8px 8px 0',
    marginLeft: '-1px',
  },
  sectionTitle: {
    fontSize: '1.125rem',
    fontWeight: '600',
    color: 'var(--text-medium)',
    borderBottom: '1px solid var(--border-color-light-gray)',
    paddingBottom: '8px',
  },
  actionButton: {
    backgroundColor: 'var(--bg-secondary)',
    border: '1px solid var(--border-color-light-gray)',
    borderRadius: '8px',
    padding: '12px',
  },
  accordionItem: { 
    border: '1px solid var(--border-color-light-gray)',
    borderRadius: '8px',
    marginBottom: '8px',
  },
  accordionButton: {
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  accordionBody: { 
    fontSize: '0.875rem',
    lineHeight: '1.5',
    color: 'var(--text-light-muted)',
  },
  contactSection: {
    padding: '16px',
    backgroundColor: 'var(--bg-light-blue)',
    border: '1px solid var(--border-color-blue)',
    borderRadius: '8px',
    marginBottom: '24px',
  },
  sidebar: {
    border: 'none',
    borderRadius: '12px',
    boxShadow: 'var(--shadow-light)',
    backgroundColor: 'var(--bg-secondary)',
    height: 'fit-content',
  },
  sidebarBody: { padding: '20px' },
  sidebarTitle: {
    fontSize: '1rem',
    fontWeight: '600',
    color: 'var(--text-medium)',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
  },
}

/**
 * Help & Support page providing documentation, FAQs, and contact information
 * Help & Support Modal component optimized for React 19+ with memoization and constants
 * Features:
 * - Quick help articles with optimized search
 * - FAQ section with controlled expansion
 * - Contact information
 * - Troubleshooting guides
 * - Links to documentation
 * Now wrapped in a modal for better UX integration
 */
const HelpSupport = memo(({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedFaq, setExpandedFaq] = useState(null)

  // Memoized filtered FAQs to prevent unnecessary re-renders
  const filteredFaqs = useMemo(() => {
    if (!searchTerm.trim()) return FAQ_ITEMS
    
    const lowerSearch = searchTerm.toLowerCase()
    return FAQ_ITEMS.filter(faq =>
      faq.question.toLowerCase().includes(lowerSearch) ||
      faq.answer.toLowerCase().includes(lowerSearch),
    )
  }, [searchTerm])

  // Memoized event handlers to prevent child re-renders
  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value)
  }, [])

  const handleClearSearch = useCallback(() => {
    setSearchTerm('')
  }, [])

  const handleToggleFaq = useCallback((faqId) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId)
  }, [expandedFaq])

  // Memoized search input with clear button
  const searchInput = useMemo(() => (
    <div className="mb-4">
      <div className="input-group" style={{ maxWidth: '400px' }}>
        <span className="input-group-text" style={STYLES.inputGroup}>
          <i className="bi bi-search"></i>
        </span>
        <input
          type="text"
          className="form-control"
          placeholder="Search help and FAQs..."
          value={searchTerm}
          onChange={handleSearchChange}
          style={STYLES.input}
        />
        {searchTerm && (
          <button
            className="btn btn-outline-secondary"
            type="button"
            onClick={handleClearSearch}
            style={STYLES.clearButton}
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>
    </div>
  ), [searchTerm, handleSearchChange, handleClearSearch])

  // Memoized quick actions section
  const quickActions = useMemo(() => (
    <div className="mb-4">
      <h4 className="mb-3" style={STYLES.sectionTitle}>
        <i className="bi bi-lightning me-2"></i>
        Quick Actions
      </h4>
      
      <div className="row g-3">
        <div className="col-md-6">
          <button 
            className="btn w-100 text-start"
            onClick={onClose}
            style={STYLES.actionButton}
          >
            <i className="bi bi-person-circle me-2 text-primary"></i>
            Account Settings
          </button>
        </div>
        
        <div className="col-md-6">
          <button 
            className="btn w-100 text-start"
            onClick={onClose}
            style={STYLES.actionButton}
          >
            <i className="bi bi-bell me-2 text-primary"></i>
            Notification Settings
          </button>
        </div>
      </div>
    </div>
  ), [onClose])

  // Memoized FAQ section
  const faqSection = useMemo(() => (
    <div className="mb-4">
      <h4 className="mb-3" style={STYLES.sectionTitle}>
        <i className="bi bi-question-circle me-2"></i>
        Frequently Asked Questions
        {searchTerm && (
          <span className="badge bg-secondary ms-2" style={{ fontSize: '0.75rem' }}>
            {filteredFaqs.length} results
          </span>
        )}
      </h4>
      
      {filteredFaqs.length > 0 ? (
        <div className="accordion" id="faqAccordion">
          {filteredFaqs.map((faq) => (
            <div key={faq.id} className="accordion-item" style={STYLES.accordionItem}>
              <h2 className="accordion-header">
                <button
                  className={`accordion-button ${expandedFaq === faq.id ? '' : 'collapsed'}`}
                  type="button"
                  onClick={() => handleToggleFaq(faq.id)}
                  style={STYLES.accordionButton}
                >
                  {faq.question}
                </button>
              </h2>
              <div
                className={`accordion-collapse collapse ${expandedFaq === faq.id ? 'show' : ''}`}
              >
                <div className="accordion-body" style={STYLES.accordionBody}>
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : searchTerm ? (
        <div className="text-center py-4">
          <i className="bi bi-search text-muted" style={{ fontSize: '2rem' }}></i>
          <p className="text-muted mt-2">No FAQs found matching &ldquo;{searchTerm}&rdquo;</p>
        </div>
      ) : null}
    </div>
  ), [filteredFaqs, searchTerm, expandedFaq, handleToggleFaq])

  // Memoized contact section
  const contactSection = useMemo(() => (
    <div style={STYLES.contactSection}>
      <div className="d-flex align-items-center mb-3">
        <i className="bi bi-envelope text-primary me-2" style={{ fontSize: '1.25rem' }}></i>
        <strong style={{ fontSize: '0.875rem', color: 'var(--color-blue-primary)' }}>Contact Support</strong>
      </div>
      
      <div className="d-flex align-items-start">
        <div className="flex-grow-1" style={{ marginLeft: '8px' }}>
          <a href="mailto:support@revatek.com" className="text-decoration-none" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
            support@revatek.com
          </a>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-light-muted)', marginTop: '4px', lineHeight: '1.4' }}>
            Send us an email and we&apos;ll respond during normal business hours US Eastern Time. We may call or email you back as needed to resolve your issue. You can also use the &ldquo;Send Feedback&rdquo; option in your account menu for general feedback and feature requests.
          </div>
        </div>
      </div>
    </div>
  ), [])

  // Memoized sidebar
  const sidebar = useMemo(() => (
    <div className="col-12 col-lg-4 col-xl-3 mt-4 mt-lg-0">
      <div className="card" style={STYLES.sidebar}>
        <div className="card-body" style={STYLES.sidebarBody}>
          <h6 className="card-title" style={STYLES.sidebarTitle}>
            <i className="bi bi-book me-2" style={{ color: 'var(--color-blue-primary)' }}></i>
            Resources
          </h6>
          
          <div className="mb-3">
            <div className="mt-2">
              <div className="d-flex align-items-center mb-2">
                <i className="bi bi-book text-primary me-2" style={{ fontSize: '0.875rem' }}></i>
                <a href="https://guide.revatek.com/" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ fontSize: '0.75rem' }}>
                  User Guide
                </a>
              </div>
              <div className="d-flex align-items-center">
                <i className="bi bi-tools text-primary me-2" style={{ fontSize: '0.875rem' }}></i>
                <a href="https://guide.revatek.com/troubleshooting" target="_blank" rel="noopener noreferrer" className="text-decoration-none" style={{ fontSize: '0.75rem' }}>
                  Troubleshooting
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ), [])

  return (
    <ModalPortal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      animation="slideUp"
      zIndex="var(--z-modal)"
    >
        <div style={{ maxHeight: 'calc(100vh - 80px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {/* Modal Header */}            <div 
            style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid var(--border-color-light-gray)',
              background: 'var(--bg-header-gradient)',
              borderRadius: '12px 12px 0 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
            }}
          >
            <h2 style={{
              fontSize: '1.5rem',
              fontWeight: '600',
              color: 'var(--text-dark)',
              margin: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}>
              <i className="bi bi-question-circle"></i>
              Help & Support
            </h2>
            <button 
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                color: 'var(--text-light-muted)',
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--text-medium)'
                e.target.style.backgroundColor = 'var(--color-hover-light)'
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--text-light-muted)'
                e.target.style.backgroundColor = 'transparent'
              }}
              aria-label="Close"
            >
              <i className="bi bi-x"></i>
            </button>
          </div>

          {/* Modal Content - Scrollable */}
          <div style={{ 
            flex: 1, 
            overflow: 'auto', 
            padding: '24px',
            background: 'var(--bg-header-gradient)',
          }}>
            <div className="row">
              <div className="col-12 col-lg-8 col-xl-9">
                {searchInput}
                {quickActions}
                {faqSection}
                {contactSection}
              </div>

              {sidebar}
            </div>
          </div>
        </div>
      </ModalPortal>
    )
})

HelpSupport.displayName = 'HelpSupport'

HelpSupport.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default HelpSupport
